import * as React from 'react';
import {Frame, FrameActions} from '@mc/react-frame';
import {Params} from '../types';
import {buildMapioUrl, serializeParams} from '../utils';
import {MapEvent} from '../events';
import {MapProps, BaseProps} from '../props';
import {dequal as deepEqual} from 'dequal';
import {InternalActions} from '../actions';

/**
 * Properties mapové komponenty.
 *
 * @ignore
 */
export type BaseMapProps<Event extends MapEvent> = BaseProps & MapProps<Event>;

/**
 * Stav mapové komponenty.
 *
 * @ignore
 */
interface BaseMapState {
    /**
     * Src atribut frame.
     */
    src?: string;
}

/**
 * Poslední vygenerované ID instance mapové komponenty.
 *
 * @ignore
 */
let lastInstanceId = 0;

/**
 * Názvy synchronizovaných properties mapové komponenty.
 *
 * @ignore
 */
const syncedPropNames = [
    'padding',
    'dateFilterItems',
    'featureActions',
    'featureTooltips',
    'externalLayersEnabled',
    'layerSettingsEnabled',
    'rasterLayersPrintMode',
    'vectorLayersPrintMode',
    'reticleVisible',
    'catalogMenuVisible',
];

/**
 * Vrátí, zda je zapnut režim ladění.
 *
 * @returns Zda je zapnut režim ladění.
 * @ignore
 */
function isDebuggingEnabled(): boolean {
    return (
        Boolean((window as any).MAPIO_DEBUG) ||
        Boolean(window.localStorage && window.localStorage.MAPIO_DEBUG) ||
        Boolean(window.sessionStorage && window.sessionStorage.MAPIO_DEBUG)
    );
}

/**
 * Předek mapových komponent.
 *
 * @typeparam ExtraProps - Dodatečné properties potomka komponenty.
 * @typeparam Event - Událost generovaná komponentou.
 * @ignore
 */
export abstract class BaseMap<ExtraProps, Event extends MapEvent> extends React.PureComponent<
    BaseMapProps<Event> & ExtraProps,
    BaseMapState
> {
    /**
     * Unikátní ID instance mapové komponenty.
     *
     * Abychom mohli rozlišit, jednotlivé instance v rámci komunikace
     * s frame skrze `postMessage`.
     */
    private instanceId: string = String(++lastInstanceId);

    /**
     * Příznak, zda je stránka v frame řádne inicializovaná a lze s ní komunikovat.
     */
    private ready: boolean = false;

    /**
     * Hodnoty synchronizovaných properties, které jsme odeslali do frame.
     */
    private syncedPropValues: {[key: string]: any} = {};

    /**
     * Stav mapové komponenty.
     */
    state: BaseMapState = {};

    /**
     * Voláno po připojení komponenty.
     */
    componentDidMount(): void {
        this.updateSrc();
    }

    /**
     * Voláno po aktualizaci komponenty.
     *
     * @param prevProps - Předchozí properties.
     */
    componentDidUpdate(prevProps: BaseMapProps<Event>): void {
        const {props} = this;

        // Změnu ostatních parametrů ignorujeme, ačkoliv se také předávají do URL pro src.
        // Tyto parametry se přes URL předávají pouze pro inicializaci prvotního stavu mapového serveru
        // a jejich následná aktualizace probíhá zasíláním zpráv přes postMessage.

        if (
            prevProps.serverUrl !== props.serverUrl ||
            serializeParams(this.getContextParamsWithAuth(prevProps)) !==
                serializeParams(this.getContextParamsWithAuth(props)) ||
            serializeParams(prevProps.queryParams) !== serializeParams(props.queryParams)
        ) {
            this.updateSrc();
        }

        this.postSyncedProps(prevProps);
    }

    /**
     * Zpracuje zprávu z frame.
     *
     * @param message - Zpráva.
     */
    private handleMessage = (message: any): void => {
        // if (!message || message.sender !== 'mapio' || String(message.instanceId) !== this.instanceId) {
        //     return; // Zpráva od jiné aplikace nebo jiné instance mapio
        // }

        if (isDebuggingEnabled()) {
            console.log(this.getDebugPrefix() + 'Received message from Mapio', message);
        }

        if (typeof message === 'string') {
            try {
                message = JSON.parse(message);
            } catch (e) {
                console.warn(this.getDebugPrefix() + 'Failed to parse message as JSON', message);
                return;
            }
        }

        switch (message.type) {
            case 'READY':
                console.log('APP READY');
                this.handleReady();
                break;

            case 'PROPERTY_CHANGE':
                this.handlePropertyChange(message.name, message.value);
                break;

            case 'EVENT':
                this.handleEvent(message.event);
                break;

            default:
                this.handleUnrecognizedMessage(message);
                break;
        }
    };

    /**
     * Zpracuje dokončení inicializace stránky ve frame.
     */
    private handleReady(): void {
        if (this.ready) {
            // Duplicitní poslání READY eventu nastává pouze při vývoji, když se v důsledku
            // hot reloadu přenačte mapio klient. Na produkci by to nemělo nikdy nastat.
            // Provedeme kompletní reset komponenty i stránky ve frame.
            console.warn(this.getDebugPrefix() + 'Unexpected reload of Frame content');
            this.clearReady();
            this.forceUpdate();
        }

        // Až teď můžeme začít komunikovat a poslat výchozí konfiguraci
        this.resumePost();
        this.ready = true;

        if (this.props.onReady) {
            this.props.onReady();
        }
    }

    /**
     * Vyresetuje stav před inicializací stránky ve frame.
     */
    clearReady(): void {
        // Resetujeme interní stav.
        this.ready = false;
        this.syncedPropValues = {};

        // Resetujeme frontu zpráv a naplánujeme nové odeslání výchozích zpráv.
        this.clearPost();
        this.suspendPost();
        this.postInitialSyncedProps();
    }

    /**
     * Zpracuje změnu property z frame.
     *
     * @param name - Název property.
     * @param value - Nová hodnota property.
     */
    private handlePropertyChange(name, value): void {
        this.syncedPropValues[name] = value;

        const capitalizedName = name.charAt(0).toUpperCase() + name.slice(1);
        const callbackName = `on${capitalizedName}Change`;
        const callback = this.props[callbackName];

        if (callback) {
            callback.bind(this)(value);
        }
    }

    /**
     * Zpracuje událost z frame.
     *
     * @param event - Událost.
     */
    private handleEvent(event: Event): void {
        const {onEvent} = this.props;
        if (onEvent) {
            onEvent(event);
        }
    }

    /**
     * Zpracuje nerozpoznanou zprávu od frame.
     *
     * @param message - Zpráva.
     */
    protected handleUnrecognizedMessage(message: any): void {
        if (message?.source === 'react-devtools-bridge') {
            // Ignorujeme zprávy od react-devtools-bridge
            return;
        }

        console.warn(this.getDebugPrefix() + 'Unrecognized message', message);
    }

    /**
     * Provede výchozí synchronizaci properties.
     */
    protected postInitialSyncedProps(): void {
        if (isDebuggingEnabled()) {
            // Propagace debug režimu i do mapio klienta.
            this.props.frameDispatch(
                FrameActions.postMessage({
                    type: '__DEBUG__',
                    value: true,
                }),
            );
        }
        this.postSyncedProps();
    }

    /**
     * Provede synchronizaci změněných properties.
     *
     * @param prevProps - Předchozí properties.
     */
    postSyncedProps(prevProps?: BaseMapProps<Event>): void {
        const {props} = this;
        for (const name of this.getSyncedPropNames()) {
            const value = this.getSyncedPropValue(props, name);
            if (value !== undefined) {
                // Synchronizujeme jen property s definovanou hodnotou.
                if (prevProps) {
                    const prevValue = this.getSyncedPropValue(prevProps, name);
                    if (!deepEqual(prevValue, value)) {
                        this.postProperty(name, value);
                    }
                } else {
                    // Prvotní synchronizace
                    this.postProperty(name, value);
                }
            }
        }
    }

    /**
     * Pošle hodnotu property do frame.
     *
     * @param name - Jméno property.
     * @param value - Hodnota property.
     */
    private postProperty(name: string, value: any): void {
        // Hodnotu v syncedPropValues zcela záměrně aktualizujeme až v handlePropertyChange.
        if (!deepEqual(this.syncedPropValues[name], value)) {
            const {frameDispatch} = this.props;
            frameDispatch(InternalActions.setProperty(name, value));
        }
    }

    /**
     * Odloží odesílání zpráv do frame.
     */
    private suspendPost(): void {
        const {frameDispatch} = this.props;
        frameDispatch(FrameActions.setPostSuspended(true));
    }

    /**
     * Obnoví odesílání zpráv do frame.
     */
    private resumePost(): void {
        const {frameDispatch} = this.props;
        frameDispatch(FrameActions.setPostSuspended(false));
    }

    /**
     * Smaže frontu zpráv do frame.
     */
    private clearPost(): void {
        const {frameDispatch} = this.props;
        frameDispatch(FrameActions.clearMessages());
    }

    /**
     * Provede aktualizaci src atributu frame.
     */
    protected updateSrc() {
        const src = this.buildSrc();
        if (src !== this.state.src) {
            this.setState({src});
            this.clearReady(); // Změna src vyvolá přenačtení frame, proto reset stavu.

            if (isDebuggingEnabled()) {
                console.log(this.getDebugPrefix() + 'Updated src', src);
            }
        }
    }

    /**
     * Vygeneruje aktuální hodnotu scr atributu frame.
     *
     * @returns URL mapového serveru.
     */
    private buildSrc(): string | undefined {
        const {serverUrl} = this.props;
        if (!serverUrl) {
            return undefined;
        }

        const path = this.getPath();
        const contextParams = this.getContextParamsWithAuth(this.props);
        const {instanceId} = this;

        const {
            queryParams,
            initialTopic,
            initialBackgroundLayer,
            initialForegroundLayer,
            initialLayers,
            initialCatalogNodes,
            initialLocation,
            initialZoom,
            initialFilter,
            topicChangeEnabled,
        } = this.props;

        return buildMapioUrl(serverUrl as string, {
            path,
            queryParams,
            contextParams,
            instanceId,
            topic: initialTopic,
            backgroundLayer: initialBackgroundLayer,
            foregroundLayer: initialForegroundLayer,
            layers: initialLayers,
            catalogNodes: initialCatalogNodes,
            location: initialLocation,
            zoom: initialZoom,
            filter: initialFilter,
            topicChangeEnabled: topicChangeEnabled,
        });
    }

    /**
     * Vrátí jména properties, které synchronizujeme s mapovým serverem při inicializaci.
     *
     * @returns Jména properties.
     */
    protected getSyncedPropNames(): string[] {
        return syncedPropNames;
    }

    /**
     * Namapuje hodnotu synchronizované property.
     *
     * @param props - Properties ze kterých chceme vzít hodnotu.
     * @param name - Název property.
     * @returns Mapovaná hodnota property.
     */
    protected getSyncedPropValue(props: BaseMapProps<Event>, name: string): any {
        return props[name];
    }

    /**
     * Vrátí prefix komponenty pro debugovací výpisy.
     *
     * @returns Prefix.
     */
    protected getDebugPrefix(): string {
        return `[${this.props.debugName}#${this.instanceId}] `;
    }

    /**
     * Vrátí cestu mapové stránky v URL.
     *
     * @returns Cesta.
     */
    protected getPath(): string {
        return '';
    }

    /**
     * Vrátí kontextové parametry pro tvorbu URL včetně autentizačních údajů.
     *
     * @param props - Properties ze kterých zkonstruovat parametry.
     * @returns Kontextové paraetry.
     */
    private getContextParamsWithAuth(props: BaseMapProps<Event>): Params | undefined {
        const {authentication, authState, contextParams} = props;

        // Fuj, prasárna a ostuda předávat autentizaci takto jako login.
        // Správně by mělo jít o časově omezený vygenerovaný token.

        let login;
        if (authentication) {
            login = authentication;
            // else if (authState && authState.login) {
        } else if (authState && authState.data?.auth) {
            login = authState.login;
        }

        if (contextParams && login) {
            return {...contextParams, login};
        }
        return login ? {login} : contextParams;
    }

    /**
     * Vykreslí frame.
     *
     * @param props - Properties komponenty.
     */
    protected renderFrame(props): JSX.Element | null {
        const {src} = this.state;
        if (!src) {
            return null;
        }

        const {
            authState,
            frameState,
            frameDispatch,
            debugName,
            serverUrl,
            queryParams,
            contextParams,
            topic,
            backgroundLayer,
            foregroundLayer,
            layers,
            catalogNodes,
            location,
            zoom,
            filter,
            padding,
            dateFilterItems,
            featureActions,
            featureTooltips,
            externalLayersEnabled,
            layerSettingsEnabled,
            catalogMenuVisible,
            rasterLayersPrintMode,
            vectorLayersPrintMode,
            reticleVisible,
            onTopicChange,
            onBackgroundLayerChange,
            onForegroundLayerChange,
            onLayersChange,
            onAllLayersChange,
            onWritableLayersChange,
            onCatalogNodesChange,
            onLocationChange,
            onZoomChange,
            onFilterChange,
            onReady,
            onEvent,
            ...frameProps
        } = props;

        return (
            <Frame
                state={frameState}
                dispatch={frameDispatch}
                src={src}
                title="Mapa"
                geolocationEnabled
                onMessage={this.handleMessage}
                {...frameProps}
            />
        );
    }
}
